import { h, render } from 'preact'
import Icon from 'snippets/icon/icon'

/**
 * Back to top button — visible after one third of the scrollable page height.
 * Visibility is driven by data-ui="visible" and data-visible: Tailwind variants.
 * Once the footer row marked [data-back-to-top-anchor] scrolls into view, the
 * button docks to it (rides up with it) instead of overlapping the bottom bar.
 */
export class BackToTopElement extends HTMLElement {
  static SCROLL_FRACTION = 1 / 5
  static ANCHOR_SELECTOR = '[data-back-to-top-anchor]'
  static DOCK_GAP = 24
  static BUTTON_CLASS =
    'btn btn-fill btn-primary btn-s w-fit whitespace-nowrap'

  static WRAPPER_CLASS =
    'absolute inset-tl pb-margin -translate-y-full opacity-0 invisible pointer-events-none transition-opacity data-visible:opacity-100 data-visible:visible data-visible:pointer-events-auto'

  private wrapper: HTMLElement | null = null
  private button: HTMLButtonElement | null = null
  private anchor: HTMLElement | null = null
  private ticking = false
  private readonly boundHandleScroll = () => this.handleScroll()
  private readonly boundHandleResize = () => this.update()
  private readonly boundHandleClick = () => this.scrollToTop()

  connectedCallback() {
    const label = this.textContent?.trim() || this.getAttribute('label') || 'Back To Top'
    this.textContent = ''
    this.renderButton(label)
    this.update()
    window.addEventListener('scroll', this.boundHandleScroll, { passive: true })
    window.addEventListener('resize', this.boundHandleResize, { passive: true })
    this.button?.addEventListener('click', this.boundHandleClick)
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.boundHandleScroll)
    window.removeEventListener('resize', this.boundHandleResize)
    this.button?.removeEventListener('click', this.boundHandleClick)
  }

  private get scrollThreshold(): number {
    const scrollable = document.documentElement.scrollHeight - window.innerHeight
    return Math.max(0, scrollable * BackToTopElement.SCROLL_FRACTION)
  }

  private renderButton(label: string) {
    this.button = document.createElement('button')
    this.button.type = 'button'
    this.button.className = BackToTopElement.BUTTON_CLASS
    this.button.setAttribute('aria-label', label)
    this.button.setAttribute('aria-hidden', 'true')

    const labelText = document.createElement('span')
    labelText.textContent = label
    this.button.appendChild(labelText)

    const iconWrap = document.createElement('span')
    iconWrap.className = 'flex w-[13px] h-[12px] children:size-full'
    render(h(Icon, { id: 'arrow-up' }), iconWrap)
    this.button.appendChild(iconWrap)

    this.wrapper = document.createElement('div')
    this.wrapper.className = BackToTopElement.WRAPPER_CLASS
    this.wrapper.appendChild(this.button)

    this.appendChild(this.wrapper)
  }

  private get dockAnchor(): HTMLElement | null {
    if (!this.anchor?.isConnected) {
      this.anchor = document.querySelector(BackToTopElement.ANCHOR_SELECTOR)
    }
    return this.anchor
  }

  private handleScroll() {
    if (this.ticking) return
    this.ticking = true
    window.requestAnimationFrame(() => {
      this.update()
      this.ticking = false
    })
  }

  private update() {
    this.updateVisibility()
    this.updateDockOffset()
  }

  /**
   * Keep the button above the footer anchor: once the anchor row approaches,
   * shift the wrapper up so its bottom edge stays DOCK_GAP above the row's top.
   * The wrapper rests at -translate-y-full above this size-0 sticky container,
   * so its resting bottom edge sits at the container's top.
   */
  private updateDockOffset() {
    if (!this.wrapper) return

    const anchor = this.dockAnchor
    if (!anchor) return

    const restingBottomEdge = this.getBoundingClientRect().top
    const dockedBottomEdge = anchor.getBoundingClientRect().top - BackToTopElement.DOCK_GAP
    const offset = Math.min(0, dockedBottomEdge - restingBottomEdge)

    this.wrapper.style.transform = offset ? `translateY(calc(-100% + ${offset}px))` : ''
  }

  private updateVisibility() {
    if (!this.button || !this.wrapper) return

    const visible = window.scrollY >= this.scrollThreshold
    if (visible) {
      this.wrapper.dataset.ui = 'visible'
      this.button.setAttribute('aria-hidden', 'false')
    } else {
      delete this.wrapper.dataset.ui
      this.button.setAttribute('aria-hidden', 'true')
    }
  }

  private scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}
